import type { IngredientFormData, IngredientValidationError } from '../../types/ingredient';

interface IngredientFormProps {
  formData: IngredientFormData;
  validationErrors: IngredientValidationError[];
  onFieldChange: (field: keyof IngredientFormData, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  isEditMode: boolean;
  /** Set of fields auto-populated from COA data; displays an indicator badge */
  autoPopulatedFields?: Set<keyof IngredientFormData>;
  /** Whether to show a required indicator on the name field */
  showRequiredIndicator?: boolean;
  /** Custom label for the submit button */
  submitLabel?: string;
  /** Whether the submit button should be disabled (in addition to isSubmitting) */
  submitDisabled?: boolean;
}

const CANNABINOID_FIELDS = [
  { key: 'thc' as const, label: 'THC %' },
  { key: 'thca' as const, label: 'THCa %' },
  { key: 'cbd' as const, label: 'CBD %' },
  { key: 'cbda' as const, label: 'CBDA %' },
  { key: 'cbg' as const, label: 'CBG %' },
  { key: 'cbn' as const, label: 'CBN %' },
];

export function IngredientForm({
  formData,
  validationErrors,
  onFieldChange,
  onSubmit,
  isSubmitting,
  isEditMode,
  autoPopulatedFields,
  showRequiredIndicator,
  submitLabel,
  submitDisabled,
}: IngredientFormProps) {
  function getError(field: keyof IngredientFormData) {
    return validationErrors.find((e) => e.field === field);
  }

  function errorId(field: keyof IngredientFormData) {
    return `ingredient-${field}-error`;
  }

  function isAutoPopulated(field: keyof IngredientFormData) {
    return autoPopulatedFields?.has(field) ?? false;
  }

  function renderAutoPopulatedBadge(field: keyof IngredientFormData) {
    if (!isAutoPopulated(field)) return null;
    return (
      <span className="auto-populated-badge" aria-label="Auto-populated from COA">
        COA
      </span>
    );
  }

  const buttonLabel = submitLabel
    ?? (isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Ingredient');

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="form-group">
        <div className="form-label-row">
          <label htmlFor="ingredient-name">
            Name
            {showRequiredIndicator && <span className="required-indicator" aria-label="required"> *</span>}
          </label>
          {renderAutoPopulatedBadge('name')}
        </div>
        <input
          id="ingredient-name"
          type="text"
          value={formData.name}
          onChange={(e) => onFieldChange('name', e.target.value)}
          maxLength={100}
          required
          aria-describedby={getError('name') ? errorId('name') : undefined}
          aria-invalid={!!getError('name')}
        />
        {getError('name') && (
          <span id={errorId('name')} className="field-error" role="alert">
            {getError('name')!.message}
          </span>
        )}
      </div>

      <div className="form-group">
        <div className="form-label-row">
          <label htmlFor="ingredient-brand">Brand</label>
          {renderAutoPopulatedBadge('brand')}
        </div>
        <input
          id="ingredient-brand"
          type="text"
          value={formData.brand}
          onChange={(e) => onFieldChange('brand', e.target.value)}
          maxLength={100}
          aria-describedby={getError('brand') ? errorId('brand') : undefined}
          aria-invalid={!!getError('brand')}
        />
        {getError('brand') && (
          <span id={errorId('brand')} className="field-error" role="alert">
            {getError('brand')!.message}
          </span>
        )}
      </div>

      <div className="form-group">
        <div className="form-label-row">
          <label htmlFor="ingredient-strain">Strain</label>
          {renderAutoPopulatedBadge('strain')}
        </div>
        <input
          id="ingredient-strain"
          type="text"
          value={formData.strain}
          onChange={(e) => onFieldChange('strain', e.target.value)}
          maxLength={100}
          aria-describedby={getError('strain') ? errorId('strain') : undefined}
          aria-invalid={!!getError('strain')}
        />
        {getError('strain') && (
          <span id={errorId('strain')} className="field-error" role="alert">
            {getError('strain')!.message}
          </span>
        )}
      </div>

      <div className="form-group">
        <div className="form-label-row">
          <label htmlFor="ingredient-varietal">Varietal</label>
          {renderAutoPopulatedBadge('varietal')}
        </div>
        <select
          id="ingredient-varietal"
          value={formData.varietal}
          onChange={(e) => onFieldChange('varietal', e.target.value)}
          aria-describedby={getError('varietal') ? errorId('varietal') : undefined}
          aria-invalid={!!getError('varietal')}
        >
          <option value="">Select varietal (optional)</option>
          <option value="Indica">Indica</option>
          <option value="Hybrid">Hybrid</option>
          <option value="Sativa">Sativa</option>
        </select>
        {getError('varietal') && (
          <span id={errorId('varietal')} className="field-error" role="alert">
            {getError('varietal')!.message}
          </span>
        )}
      </div>

      <fieldset className="cannabinoid-section">
        <legend>Cannabinoid Profile (%)</legend>
        {CANNABINOID_FIELDS.map(({ key, label }) => (
          <div className="form-group" key={key}>
            <div className="form-label-row">
              <label htmlFor={`ingredient-${key}`}>{label}</label>
              {renderAutoPopulatedBadge(key)}
            </div>
            <input
              id={`ingredient-${key}`}
              type="number"
              value={formData[key]}
              onChange={(e) => onFieldChange(key, e.target.value)}
              min={0}
              max={100}
              step="0.01"
              placeholder="0.00"
              aria-describedby={getError(key) ? errorId(key) : undefined}
              aria-invalid={!!getError(key)}
            />
            {getError(key) && (
              <span id={errorId(key)} className="field-error" role="alert">
                {getError(key)!.message}
              </span>
            )}
          </div>
        ))}
      </fieldset>

      <button
        type="submit"
        className="cta-button"
        disabled={isSubmitting || submitDisabled}
      >
        {isSubmitting ? 'Saving...' : buttonLabel}
      </button>
    </form>
  );
}
